import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import EndLocalConsultationDialogComponent, {
  EndLocalConsultationDialogProps as EndLocalConsultationDialogComponentProps,
} from '@/components/ui/dialogs/EndLocalConsultationDialog/EndLocalConsultationDialog'
import { useAppSelector } from '@/redux/rootReducer'
import { endConsultation, selectConsultationById, selectConsultationUpdateRequestTracker } from '@/redux/reducers/consultation.reducer'
import { createExpertise, selectExpertiseCreateRequestTracker, selectNewlyCreatedExpertiseId } from '@/redux/reducers/expertise.reducer'
import ExpertiseUpdateDialog from '@/pages/expertise/ExpertiseUpdateDialog'
import { useIsWebView } from '@/hooks/useSizeBreakpoints'

type EndLocalConsultationDialogProps = Omit<EndLocalConsultationDialogComponentProps, 'isEnding' | 'onEndLiveConsultation' | 'onGenerateExpertise'> & {
  consultationId?: string
}

export function EndLocalConsultationDialog(props: EndLocalConsultationDialogProps) {
  const { consultationId, onClose } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const isWebView = useIsWebView()
  const [expertiseDialog, setExpertiseDialog] = useState(false)

  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const newlyCreatedExpertiseId = useAppSelector(selectNewlyCreatedExpertiseId)

  const endConsultationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.consultation.end.success'), { variant: 'success' })
      navigate('/consultations/local')
    },
    onError: () => enqueueSnackbar(t('notification.consultation.end.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const createExpertiseRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.expertise.create.success'), { variant: 'success' })
      if (!isWebView) window.open(`/expertises/${newlyCreatedExpertiseId}/update`, '_blank')
      else setExpertiseDialog(true)
    },
    onError: () => enqueueSnackbar(t('notification.consultation.end.error'), { variant: 'error' }),
    requestTrackerSelector: selectExpertiseCreateRequestTracker,
  })

  const handleGenerateExpertise = () => {
    if (consultation)
      createExpertiseRequestHandler.dispatch(
        createExpertise({
          expertise: {
            patient: consultation.patient._id,
            consultations: [consultation._id],
          },
        })
      )
    onClose?.()
  }

  const handleEndLocalConsultation = () => {
    if (consultationId) endConsultationRequestHandler.dispatch(endConsultation(consultationId))
  }

  const handleClose = () => {
    navigate('/')
  }

  const onCloseExpertise = () => setExpertiseDialog(false)

  return (
    <>
      <EndLocalConsultationDialogComponent
        {...props}
        isEnding={endConsultationRequestHandler.requestTracker.loading}
        onEndConsultation={handleEndLocalConsultation}
        onGenerateExpertise={handleGenerateExpertise}
        onQuit={handleClose}
      />
      <ExpertiseUpdateDialog expertiseId={newlyCreatedExpertiseId} open={expertiseDialog} onClose={onCloseExpertise} />
    </>
  )
}

export default EndLocalConsultationDialog
